import {
  Box,
  FormControl,
  FormHelperText,
  InputAdornment,
  type InputBaseComponentProps,
  InputLabel,
  MenuItem,
  makeStyles,
  OutlinedInput,
  Select,
} from "@material-ui/core";
import React, { useMemo } from "react";
import HelperText from "./HelperText";

interface LabelParserProps {
  label: string;
}

const LabelParser = ({ label }: LabelParserProps): React.ReactElement => {
  // superscript
  const superscriptRegex = /\^\d+/g;
  const supMatches = [...label.matchAll(superscriptRegex)];

  if (!supMatches.length) {
    return <>{label}</>;
  }

  const accumulator = <></>;
  const parsedLabel = supMatches.reduce((acc, cur, curIndex) => {
    const [matchedString] = cur;
    const { index } = cur;
    const prev = curIndex === 0 ? { index: -2 } : supMatches[curIndex - 1];
    const { index: pIndex } = prev;

    return (
      <>
        {acc}
        {label.substr(pIndex! + 2, index! - pIndex! - 2)}
        <sup>{matchedString.replace("^", "")}</sup>
        {curIndex === supMatches.length - 1 ? label.substr(index! + 2) : null}
      </>
    );
  }, accumulator);

  return parsedLabel;
};

const LabelParserMemo = React.memo(LabelParser);

export interface Unit {
  title?: string;
  value?: string;
}

const useUnitStyles = makeStyles({
  root: {
    minWidth: ({ longestUnit }: { longestUnit?: Unit }) =>
      `${longestUnit?.title?.length ?? 2}ch`,
  },
});

// Ensures that the unit field is always
// at least the width of the longest option
function useUnitClasses({ units }: { units: Unit[] }) {
  const longestUnit = useMemo(
    () =>
      units?.reduce(
        (lastUnit, currentUnit) => {
          return (currentUnit.title?.length ?? 0) >
            (lastUnit.title?.length ?? 0)
            ? currentUnit
            : lastUnit;
        },
        { title: "" },
      ),
    [units],
  );
  return useUnitStyles({ longestUnit });
}

export interface UnitSelectProps {
  name: string;
  required?: boolean;
  disabled?: boolean;
  onChange?: React.ChangeEventHandler<{ name?: string; value: unknown }>;
  onFocus?: React.FocusEventHandler;
  onBlur?: React.FocusEventHandler;
  units?: Unit[];
  value: string;
}

const UnitSelect = (props: UnitSelectProps) => {
  const {
    name,
    onChange = undefined,
    onFocus = undefined,
    onBlur = undefined,
    units = [],
    value,
    required = false,
    disabled = false,
  } = props;
  const classes = useUnitClasses({ units });

  if (!units?.length) {
    return null;
  }

  if (units?.length === 1) {
    return <LabelParserMemo label={units[0].title!} />;
  }

  return (
    <Select
      required={required}
      disabled={disabled}
      id={name}
      name={name}
      value={value ?? ""}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      classes={classes}
      disableUnderline
    >
      {units.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          <LabelParserMemo label={option.title!} />
        </MenuItem>
      ))}
    </Select>
  );
};

export interface UnitInputProps {
  name: string;
  type?: "string" | "number";
  units: Unit[];
  error?: string;
  helperText?: string;
  label?: string;
  onChangeUnit?: React.ChangeEventHandler<HTMLSelectElement>;
  onChangeValue?: React.ChangeEventHandler<HTMLInputElement>;
  onFocusUnit?: React.FocusEventHandler<HTMLSelectElement>;
  onFocusValue?: React.FocusEventHandler<HTMLInputElement>;
  onBlurUnit?: React.FocusEventHandler<HTMLSelectElement>;
  onBlurValue?: React.FocusEventHandler<HTMLInputElement>;
  value?: {
    value?: string | number;
    unit?: string;
  };
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
}

const UnitInput = (props: UnitInputProps): React.ReactElement => {
  const {
    name,
    type = "number",
    units,
    error = undefined,
    helperText = "",
    onChangeUnit: handleChangeUnit = undefined,
    onChangeValue: handleChangeValue = undefined,
    onFocusUnit: handleFocusUnit = undefined,
    onFocusValue: handleFocusValue = undefined,
    onBlurUnit: handleBlurUnit = undefined,
    onBlurValue: handleBlurValue = undefined,
    value = { value: "", unit: "" },
    label = undefined,
    disabled = false,
    required,
    fullWidth = false,
  } = props;
  const { value: inputValue, unit: unitValue } = value ?? {
    value: undefined,
    unit: undefined,
  };
  const hasError = !!error;

  /**
   * Assuming the the most common use of this input when the type is numeric
   * is to only allow inputs that match the pattern below.
   * See more on this: https://next.material-ui.com/components/text-fields/#type-quot-number-quot
   */
  const inputProps: InputBaseComponentProps =
    type === "number" ? { inputMode: "numeric" as const } : {};

  return (
    <Box display="flex">
      <FormControl
        fullWidth={fullWidth}
        required={required}
        size="small"
        variant="outlined"
        error={hasError}
      >
        <InputLabel shrink required={required} htmlFor={name}>
          {label}
        </InputLabel>
        <OutlinedInput
          id={name}
          inputProps={inputProps}
          name={`${name}.value`}
          type="number"
          value={inputValue ?? ""}
          onChange={handleChangeValue}
          onFocus={handleFocusValue}
          onBlur={handleBlurValue}
          required={required}
          disabled={disabled}
          endAdornment={
            <InputAdornment position="end">
              <UnitSelect
                name={`${name}.unit`}
                required={required}
                units={units}
                value={unitValue ?? ""}
                onChange={handleChangeUnit}
                onFocus={handleFocusUnit}
                onBlur={handleBlurUnit}
                disabled={disabled}
              />
            </InputAdornment>
          }
        />
        <FormHelperText>
          <HelperText error={error}>{helperText}</HelperText>
        </FormHelperText>
      </FormControl>
    </Box>
  );
};

export default UnitInput;
